import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import {
  ArrowLeft,
  ShoppingCart,
  Check,
  Truck,
  Shield,
  Factory,
  Minus,
  Plus,
  Loader2,
  AlertTriangle,
  Info,
  ShieldCheck,
  ZoomIn,
} from "lucide-react";
import MaterialComparison from "../components/MaterialComparison";
import MaterialSwitchTabs from "../components/MaterialSwitchTabs";
import HpComparator from "../components/HpComparator";
import ZoomLightbox from "../components/ZoomLightbox";
import useScrollDirection from "../hooks/useScrollDirection";
import useSeoHead from "../hooks/useSeoHead";
import { qtyMatchesFullBoxes, piecesUntilNextBox } from "../lib/pricing";
import { useDistributor } from "../context/DistributorContext";

const KIT_BADGE_COLOR = {
  set: "border-emerald-300 bg-emerald-50 text-emerald-800",
  "set+stud": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "set+thread": "border-emerald-300 bg-emerald-50 text-emerald-800",
  din3567: "border-emerald-300 bg-emerald-50 text-emerald-800",
  "din3567-set": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "din3567-half": "border-amber-400 bg-amber-50 text-amber-900",
  half: "border-amber-400 bg-amber-50 text-amber-900",
  cap: "border-sky-300 bg-sky-50 text-sky-800",
  other: "border-zinc-300 bg-zinc-50 text-zinc-700",
};

// Display labels for the material badge that follows the product name.
// 304 → V2A-304 · 316 → V4A-316 · Duplex → DUPLEX-1.4462 · Galva → GALVA
const MATERIAL_LABEL = (m) => {
  const k = (m || "").toLowerCase();
  if (k.includes("316")) return "V4A-316";
  if (k.includes("304")) return "V2A-304";
  if (k.includes("duplex")) return "DUPLEX-1.4462";
  if (k.includes("galva") || k.includes("gegalv")) return "GALVA";
  return (m || "").toUpperCase();
};

const STOCK_STATUS_KEYS = {
  in_stock: { tKey: "product.stock_in", dot: "bg-emerald-500", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  lead_time: { tKey: "product.stock_lead", dot: "bg-amber-500", cls: "border-amber-300 bg-amber-50 text-amber-800" },
  on_request: { tKey: "product.stock_request", dot: "bg-zinc-400", cls: "border-zinc-300 bg-zinc-100 text-zinc-700" },
};

function StockBadge({ status }) {
  const { t } = useTranslation();
  const s = STOCK_STATUS_KEYS[status] || STOCK_STATUS_KEYS.in_stock;
  return (
    <div className="mt-3" data-testid={`stock-badge-${status || "in_stock"}`}>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-wider ${s.cls}`}
      >
        <span className={`h-2 w-2 rounded-full ${s.dot}`} aria-hidden />
        {t(s.tKey)}
      </span>
    </div>
  );
}

function SpecCell({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-mono text-xs font-bold text-zinc-900">{value}</div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dnContext = searchParams.get("dn");
  const d1MmContext = searchParams.get("d1_mm");
  const inchContext = searchParams.get("inch");
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [search, setSearch] = useState("");
  const [stripFilter, setStripFilter] = useState("all");
  const [zoomOpen, setZoomOpen] = useState(false);
  // Mobile sticky bar visibility — hides when scrolling down, reappears when scrolling up
  const stickyVisible = useScrollDirection();

  // Distributor pricing overlay (active only when logged in)
  const { user: distributor, api: distApi } = useDistributor();
  const [distPricing, setDistPricing] = useState(null);  // { variants: [...], markup_pct }

  useEffect(() => {
    if (!product?.id || !distributor || distributor === false) {
      setDistPricing(null);
      return;
    }
    distApi
      .get(`/distributors/me/pricing/${product.id}`)
      .then((r) => setDistPricing(r.data))
      .catch(() => setDistPricing(null));
  }, [product?.id, distributor, distApi]);

  useEffect(() => {
    setLoading(true);
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        // Auto-select the variant best matching the URL context. Priority:
        //   1) exact diameter match (d1_mm)  — most precise
        //   2) inch match (e.g. 1/2")
        //   3) DN match (DN15)
        let idx = 0;
        if (p?.variants?.length) {
          if (d1MmContext) {
            const d = parseFloat(d1MmContext);
            const matchIdx = p.variants.findIndex(
              (v) => Math.abs((v.diameter_mm || 0) - d) < 0.01
            );
            if (matchIdx >= 0) idx = matchIdx;
          }
          if (idx === 0 && inchContext) {
            const matchIdx = p.variants.findIndex((v) => v.inch === inchContext);
            if (matchIdx >= 0) idx = matchIdx;
          }
          if (idx === 0 && dnContext) {
            const dnNum = parseInt(dnContext);
            const matchIdx = p.variants.findIndex((v) => v.dn === dnNum);
            if (matchIdx >= 0) idx = matchIdx;
          }
        }
        setVariantIdx(idx);
        setQty(1);
        setStripFilter("all");
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id, dnContext, d1MmContext, inchContext]);

  useEffect(() => {
    if (variantIdx > 0 && typeof document !== "undefined") {
      // Scroll the preselected variant row into view inside its scrollable list
      const el = document.querySelector(
        `[data-testid="variant-${product?.variants?.[variantIdx]?.sku}"]`
      );
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, variantIdx]);

  const filteredVariants = useMemo(() => {
    if (!product) return [];
    let list = product.variants;
    if (stripFilter !== "all") {
      list = list.filter((v) => v.strip_size === stripFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.sku.toLowerCase().includes(s) ||
          (v.diameter_mm && String(v.diameter_mm).includes(s)) ||
          (v.dn && String(v.dn).includes(s)) ||
          (v.inch && v.inch.toLowerCase().includes(s))
      );
    }
    // Always sort by pipe diameter ASC, then by strip size (width × thickness)
    // so when "Alle" is selected the customer sees every strip variant for
    // the same DN clustered together before stepping to the next diameter.
    const stripRank = (s) => {
      if (!s) return [9999, 9999];
      const m = String(s).match(/(\d+)\s*[x×]\s*(\d+)/i);
      if (!m) return [9999, 9999];
      return [parseInt(m[1], 10), parseInt(m[2], 10)];
    };
    return [...list].sort((a, b) => {
      const da = Number(a.diameter_mm ?? a.d1_mm ?? a.dn ?? 0);
      const db = Number(b.diameter_mm ?? b.d1_mm ?? b.dn ?? 0);
      if (da !== db) return da - db;
      const [aw, at] = stripRank(a.strip_size);
      const [bw, bt] = stripRank(b.strip_size);
      if (aw !== bw) return aw - bw;
      if (at !== bt) return at - bt;
      return (a.sku || "").localeCompare(b.sku || "");
    });
  }, [product, search, stripFilter]);

  const variant = product && product.variants[variantIdx];
  const boxQty = variant?.box_qty || 0;
  const hasBoxDeal = boxQty >= 2;
  const boxDiscountPct = product?.box_discount_pct || 0;

  // When the customer reaches the box quantity (or more), the discounted
  // box_unit_price applies; below that, the standard piece price.
  // For active distributors, distributor_price (cost × markup) replaces ALL
  // pricing — omdoos-korting does not stack on top.
  const fullBoxAligned = qtyMatchesFullBoxes(qty, boxQty);
  const distVariantPrice = distPricing?.variants?.find((v) => v.sku === variant?.sku)?.distributor_price;
  const retailUnitPrice = variant
    ? hasBoxDeal && fullBoxAligned
      ? variant.box_unit_price ?? variant.price
      : variant.price
    : 0;
  const effectiveUnitPrice = distVariantPrice != null ? distVariantPrice : retailUnitPrice;
  const isDistributorPricing = distVariantPrice != null;
  const piecesToNextBox = piecesUntilNextBox(qty, boxQty);

  const handleAdd = () => {
    if (!product || !variant) return;
    const stripLabel = variant.strip_size ? ` · ${variant.strip_size}` : "";
    // Prefer the variant-specific image override (uploaded per-SKU via admin)
    // so the cart thumbnail always matches what the customer is looking at on
    // the product page.
    const cartImage = variant.image_override_url
      ? `${process.env.REACT_APP_BACKEND_URL || ""}${variant.image_override_url}`
      : product.image;
    addItem({
      product_id: product.id,
      sku: variant.sku,
      name: `${product.name_nl || product.name} — Ø${variant.diameter_mm}mm${stripLabel}`,
      quantity: qty,
      unit_price: effectiveUnitPrice,
      image: cartImage,
      kit_type: product.kit_type,
      kit_label_nl: product.kit_label_nl,
      is_half: product.is_half,
      meta: {
        dn: variant.dn,
        inch: variant.inch,
        diameter_mm: variant.diameter_mm,
        weight_g: variant.weight_g,
        strip_size: variant.strip_size,
        box_qty: boxQty,
        box_unit_price: variant.box_unit_price,
        piece_price: variant.price,
        box_discount_pct: boxDiscountPct,
        pack_size: variant.pack_size || 0,
        subpack_surcharge: product.subpack_surcharge ?? 24.5,
      },
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }
  if (!product) {
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-20 text-center">
        <h2 className="font-display text-3xl text-zinc-900">Product niet gevonden</h2>
        <Link to="/products" className="mt-4 inline-block text-nibro-sage-dark hover:underline">
          Terug naar catalogus
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24 sm:pb-0" data-testid="product-detail-page">
      <ProductSeo product={product} variant={variant} effectiveUnitPrice={effectiveUnitPrice} />
      <div className="mx-auto max-w-[1400px] px-5 py-8 lg:px-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          data-testid="back-button"
        >
          <ArrowLeft size={14} /> Terug
        </button>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Image + kit warning */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              data-testid="product-hero-zoom-trigger"
              aria-label="Foto vergroten"
              className="group relative block aspect-square w-full overflow-hidden rounded-md border border-zinc-200 bg-white p-8 transition hover:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/40"
            >
              <img
                src={
                  (variant?.image_override_url
                    ? `${process.env.REACT_APP_BACKEND_URL || ""}${variant.image_override_url}`
                    : product.image)
                }
                alt={
                  variant?.sku
                    ? t("product.image_alt_variant", { name: product.name, material: product.material || "", sku: variant.sku })
                    : (product.material
                        ? t("product.image_alt", { name: product.name, material: product.material })
                        : t("product.image_alt_no_material", { name: product.name }))
                }
                className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                data-testid="product-hero-image"
              />
              <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                <span className="rounded border border-nibro-sage bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-nibro-sage-dark">
                  {product.material}
                </span>
                {product.standard && (
                  <span className="rounded border border-aramfix-red/40 bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-aramfix-red">
                    {product.standard}
                  </span>
                )}
              </div>
              {/* Zoom affordance — always visible on touch, fades in on hover/focus on desktop */}
              <span
                className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white/95 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-zinc-700 shadow-sm transition opacity-0 group-hover:opacity-100 group-focus:opacity-100 max-sm:opacity-100"
                data-testid="product-hero-zoom-hint"
              >
                <ZoomIn size={12} className="text-nibro-sage-dark" aria-hidden />
                Klik om te vergroten
              </span>
            </button>

            <ZoomLightbox
              open={zoomOpen}
              onClose={() => setZoomOpen(false)}
              src={
                variant?.image_override_url
                  ? `${process.env.REACT_APP_BACKEND_URL || ""}${variant.image_override_url}`
                  : product.image
              }
              alt={
                variant?.sku
                  ? t("product.image_alt_variant", { name: product.name, material: product.material || "", sku: variant.sku })
                  : (product.material
                      ? t("product.image_alt", { name: product.name, material: product.material })
                      : t("product.image_alt_no_material", { name: product.name }))
              }
            />

            {/* Kit type warning — prominent */}
            <div
              className={`flex items-start gap-3 rounded-md border-2 px-4 py-4 text-sm ${
                KIT_BADGE_COLOR[product.kit_type] || KIT_BADGE_COLOR.other
              }`}
              data-testid="kit-warning"
            >
              {product.is_half ? (
                <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" />
              ) : (
                <Info size={20} className="mt-0.5 flex-shrink-0" />
              )}
              <div>
                <div className="font-display text-sm font-bold uppercase tracking-wide">
                  {product.is_half ? t("product.half_clamp_warning_title", { defaultValue: "Let op — Halve beugel" }) : t("product.delivery", { defaultValue: "Levering" })}
                </div>
                <div className="mt-1 leading-relaxed">{product.kit_warning}</div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="label-uppercase mb-3">
              {product.category} · {product.material}
              {product.strip_sizes && product.strip_sizes.length > 1
                ? ` · ${product.strip_sizes.join(" / ")}`
                : ` · ${product.strip_size || "—"}`}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">
              {product.name_nl || product.name}{" "}
              <span
                className="ml-1 inline-block rounded-md border-2 border-nibro-sage-dark bg-nibro-sage-pale px-2.5 py-1 align-middle font-mono text-base font-bold text-nibro-sage-dark"
                data-testid="material-badge"
              >
                {MATERIAL_LABEL(product.material)}
              </span>
            </h1>
            <MaterialSwitchTabs
              productId={product.id}
              currentMaterial={product.material}
              dnContext={dnContext}
            />
            <StockBadge status={product.stock_status} />
            <p className="mt-4 leading-relaxed text-zinc-600">{product.description}</p>

            {/* Distributor pricing banner */}
            {isDistributorPricing && (
              <div
                className="mt-5 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm"
                data-testid="distributor-pricing-banner"
              >
                <div className="flex items-start gap-2">
                  <ShieldCheck size={16} className="mt-0.5 flex-shrink-0 text-emerald-700" />
                  <div className="text-emerald-900">
                    <strong>Distributeurprijzen actief</strong> ({distPricing?.markup_pct?.toFixed(1)}% opslag op kostprijs).
                    Doorgehaalde prijzen tonen de reguliere bruto-prijs.
                  </div>
                </div>
              </div>
            )}

            {/* Omdoos-korting info — only when variant has a meaningful box qty AND not on distributor pricing */}
            {hasBoxDeal && !isDistributorPricing && (
              <div
                className="mt-5 rounded-md border border-aramfix-red/30 bg-aramfix-red/5 px-4 py-3 text-sm"
                data-testid="box-discount-info"
              >
                <div className="flex items-start gap-2">
                  <Info size={16} className="mt-0.5 flex-shrink-0 text-aramfix-red" />
                  <div className="text-zinc-800">
                    <strong>{t("product.box_discount_short", { defaultValue: "Omdoos-korting" })} {boxDiscountPct.toFixed(0)}%</strong> {t("product.box_discount_at", { defaultValue: "bij" })} {boxQty} / {boxQty * 2} / {boxQty * 3} {t("product.pieces_per_box", { defaultValue: "stuks (per volle omdoos)" })}.
                  </div>
                </div>
              </div>
            )}

            {/* Strip-size filter chips — match the red inline badges in the
                variant list so the visual link between filter and row is
                unambiguous. */}
            {product.strip_sizes && product.strip_sizes.length > 1 && (
              <div className="mt-5" data-testid="strip-filter">
                <div className="label-uppercase mb-2">{t("product.strip_size_filter")}</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setStripFilter("all")}
                    data-testid="strip-filter-all"
                    className={`rounded border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider transition ${
                      stripFilter === "all"
                        ? "border-zinc-700 bg-zinc-700 text-white"
                        : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-500"
                    }`}
                  >
                    {t("product.all")} ({product.variants.length})
                  </button>
                  {product.strip_sizes.map((s) => {
                    const count = product.variants.filter((v) => v.strip_size === s).length;
                    const active = stripFilter === s;
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setStripFilter(s)}
                        data-testid={`strip-filter-${s}`}
                        title={`Toon alleen ${count} maten met stripmaat ${s}`}
                        className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[11px] font-bold transition ${
                          active
                            ? "border-aramfix-red bg-aramfix-red text-white"
                            : "border-aramfix-red/40 bg-aramfix-red/5 text-aramfix-red hover:bg-aramfix-red/10"
                        }`}
                      >
                        {s}
                        <span className={`font-mono text-[9px] font-normal ${active ? "text-white/85" : "text-aramfix-red/70"}`}>
                          ({count})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variant table */}
            <div className="mt-6 rounded-md border border-zinc-200 bg-white">
              <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
                <div className="label-uppercase">
                  {t("product.all_sizes_count", { n: product.variant_count, from: product.price_from.toFixed(2), to: product.price_to.toFixed(2) })}
                </div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("product.search_size")}
                  className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs focus:border-nibro-sage focus:outline-none"
                  data-testid="variant-search"
                />
              </div>
              <div className="max-h-[360px] overflow-y-auto" data-testid="variant-list">
                {filteredVariants.length === 0 ? (
                  <div className="p-6 text-center text-sm text-zinc-500">{t("product.no_sizes_found", { defaultValue: "Geen maten gevonden" })}</div>
                ) : (
                  filteredVariants.map((v, i) => {
                    const actualIdx = product.variants.indexOf(v);
                    const dp = distPricing?.variants?.find((x) => x.sku === v.sku)?.distributor_price;
                    // Insert a subtle separator above the first row of every
                    // new diameter group — makes it instantly obvious which
                    // stripmaten belong to the same DN when "Alle" is active.
                    const prev = i > 0 ? filteredVariants[i - 1] : null;
                    const isGroupStart = !prev || prev.diameter_mm !== v.diameter_mm;
                    return (
                      <button
                        type="button"
                        key={v.sku}
                        onClick={() => setVariantIdx(actualIdx)}
                        className={`grid w-full grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 border-b border-zinc-50 px-3 py-3 text-left transition hover:bg-nibro-sage-pale sm:grid-cols-[130px_110px_120px_90px_1fr] sm:gap-3 sm:px-4 sm:py-2.5 ${
                          isGroupStart && i > 0 ? "border-t-2 border-t-zinc-200" : ""
                        } ${
                          actualIdx === variantIdx ? "bg-nibro-sage-pale" : ""
                        }`}
                        data-testid={`variant-${v.sku}`}
                      >
                        {/* Col 1 — SKU */}
                        <div className="font-mono text-xs font-bold text-zinc-800">{v.sku}</div>

                        {/* Cols 2-4 (mobile: ONE row with diameter left, DN middle, strip right — fixed alignment) */}
                        <div className="col-span-2 grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:contents">
                          <div className="font-mono text-sm text-zinc-700">Ø {v.diameter_mm} mm</div>
                          <div className="text-left sm:text-left">
                            {v.dn || v.inch ? (
                              <span className="inline-block rounded bg-nibro-sage-pale px-2 py-0.5 font-mono text-[10px] font-bold text-nibro-sage-dark">
                                {v.dn ? `DN${v.dn}` : ""}
                                {v.dn && v.inch ? " · " : ""}
                                {v.inch || ""}
                              </span>
                            ) : (
                              <span className="font-mono text-[10px] text-zinc-300">—</span>
                            )}
                          </div>
                          <div className="justify-self-end sm:justify-self-start">
                            {v.strip_size ? (
                              <span className="inline-block rounded border border-aramfix-red/40 bg-aramfix-red/5 px-2 py-0.5 font-mono text-[10px] font-bold text-aramfix-red">
                                {v.strip_size}
                              </span>
                            ) : (
                              <span className="hidden font-mono text-[10px] text-zinc-300 sm:inline">—</span>
                            )}
                          </div>
                        </div>

                        {/* Col 5 — Price (rightmost, both mobile & desktop) */}
                        <div className="row-start-1 col-start-2 text-right sm:row-auto sm:col-auto">
                          {v.price_on_request ? (
                            <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[10px] font-bold text-amber-800" data-testid={`variant-on-request-${v.sku}`}>
                              Op aanvraag
                            </span>
                          ) : dp != null ? (
                            <>
                              <div className="font-display text-base font-bold text-emerald-700">€{dp.toFixed(2)}</div>
                              <div className="hidden font-mono text-[10px] text-zinc-400 line-through sm:block">€{v.price.toFixed(2)}</div>
                            </>
                          ) : (
                            <>
                              <div className="font-display text-base font-bold text-zinc-900">€{v.price.toFixed(2)}</div>
                              <div className="font-mono text-[10px] text-zinc-400">{v.weight_g} g</div>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Add to cart */}
            <div className="mt-6 rounded-md border-2 border-nibro-sage bg-white p-5">
              {variant && (
                <>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="label-uppercase">{t("product.selected")}</div>
                      <div className="font-mono text-xs text-zinc-500">
                        {variant.sku}
                        {variant.strip_size ? ` · ${variant.strip_size}` : ""}
                      </div>
                      <div className="mt-1 font-display text-lg font-bold text-zinc-900">
                        Ø {variant.diameter_mm} mm{variant.dn ? ` · DN${variant.dn}` : ""}{variant.inch ? ` · ${variant.inch}` : ""}
                      </div>
                      <div className="mt-2 font-display text-3xl font-bold text-zinc-900" data-testid="effective-unit-price">
                        {variant.price_on_request ? (
                          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 font-mono text-sm text-amber-800">
                            <AlertTriangle size={14} /> {t("product.price_on_request")}
                          </span>
                        ) : (
                          <>
                            €{effectiveUnitPrice.toFixed(2)} <span className="text-sm text-zinc-500 font-mono">{t("product.per_piece")}</span>
                          </>
                        )}
                      </div>
                      {isDistributorPricing && retailUnitPrice > effectiveUnitPrice && (
                        <div className="mt-1 flex items-center gap-2 text-xs" data-testid="distributor-price-savings">
                          <span className="text-zinc-500 line-through">€{retailUnitPrice.toFixed(2)}</span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700">
                            <ShieldCheck size={10} /> Uw distributeur-prijs (−{Math.round(((retailUnitPrice - effectiveUnitPrice) / retailUnitPrice) * 100)}%)
                          </span>
                        </div>
                      )}
                      {hasBoxDeal && !isDistributorPricing && (
                        <div className="mt-1 text-xs text-zinc-500">
                          {t("product.line_total", { defaultValue: "Lijntotaal" })}: <strong className="text-zinc-900">€{(effectiveUnitPrice * qty).toFixed(2)}</strong>{" "}
                          {fullBoxAligned ? (
                            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                              <Check size={10} /> {t("product.discount_active", { pct: boxDiscountPct.toFixed(0) })}
                            </span>
                          ) : qty > boxQty ? (
                            <button
                              type="button"
                              onClick={() => setQty(qty + piecesToNextBox)}
                              className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 hover:bg-amber-100"
                              data-testid="qty-fill-next-box"
                            >
                              {t("product.pieces_to_next_box", { n: piecesToNextBox })}
                            </button>
                          ) : (
                            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold text-zinc-600">
                              {t("cart.piece_price")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center self-start rounded-md border border-zinc-200 sm:self-auto">
                      <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-zinc-500 hover:text-zinc-900 sm:p-2.5" data-testid="qty-decrement"><Minus size={14} /></button>
                      <input type="number" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 bg-transparent text-center font-mono text-zinc-900 focus:outline-none sm:w-14" data-testid="qty-input" />
                      <button type="button" onClick={() => setQty(qty + 1)} className="p-3 text-zinc-500 hover:text-zinc-900 sm:p-2.5" data-testid="qty-increment"><Plus size={14} /></button>
                    </div>
                  </div>

                  {/* Box-discount preset buttons */}
                  {!variant.price_on_request && hasBoxDeal && (
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="dual-pricing">
                      <button
                        type="button"
                        onClick={() => setQty(1)}
                        data-testid="qty-preset-single"
                        className={`rounded-md border-2 px-4 py-3 text-left transition ${
                          !fullBoxAligned
                            ? "border-zinc-400 bg-zinc-50"
                            : "border-zinc-200 bg-white hover:border-zinc-400"
                        }`}
                      >
                        <div className="label-uppercase text-zinc-600">{t("product.buy_piece")}</div>
                        <div className="mt-1 font-display text-xl font-bold text-zinc-900">
                          €{variant.price.toFixed(2)} <span className="text-xs font-normal text-zinc-500 font-mono">{t("product.per_piece")}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500">{t("product.buy_piece_helper")}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setQty(boxQty)}
                        data-testid="qty-preset-box"
                        className={`rounded-md border-2 px-4 py-3 text-left transition ${
                          fullBoxAligned
                            ? "border-aramfix-red bg-aramfix-red/5"
                            : "border-zinc-200 bg-white hover:border-aramfix-red/50"
                        }`}
                      >
                        <div className="label-uppercase text-aramfix-red">
                          {t("product.buy_box_short", { n: boxQty })}
                          <span className="ml-1 rounded-full bg-aramfix-red px-1.5 py-0.5 text-[9px] font-bold text-white">
                            −{boxDiscountPct.toFixed(0)}%
                          </span>
                        </div>
                        <div className="mt-1 font-display text-xl font-bold text-zinc-900">
                          €{(variant.box_total_price || variant.price * boxQty).toFixed(2)}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          {t("product.equals_per_piece", { price: (variant.box_unit_price || variant.price).toFixed(2) })}
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Inline kit reminder */}
                  {product.is_half && (
                    <div className="mt-4 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>Halve beugel</strong> — voor een volledige montage heeft u 2 helften (boven + onder) + bouten en moeren nodig.
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={variant.price_on_request
                      ? () => { window.location.href = `/contact?sku=${encodeURIComponent(variant.sku)}&subject=Offerteaanvraag%20${encodeURIComponent(variant.sku)}`; }
                      : handleAdd}
                    className={`mt-5 flex w-full items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-bold transition ${
                      variant.price_on_request
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : added
                          ? "bg-emerald-500 text-white"
                          : "bg-nibro-sage text-white hover:bg-nibro-sage-hover"
                    }`}
                    data-testid={variant.price_on_request ? "request-quote-button" : "add-to-cart-button"}
                  >
                    {variant.price_on_request ? (
                      <><AlertTriangle size={16} /> {t("product.request_quote")}</>
                    ) : added ? (
                      <><Check size={16} /> {t("common.added")}</>
                    ) : (
                      <><ShoppingCart size={16} /> {t("common.add_to_cart")}</>
                    )}
                  </button>

                  <div className="mt-4 grid grid-cols-3 gap-3 text-xs text-zinc-500">
                    <div className="flex items-center gap-2"><Truck size={14} /> {t("product.delivery_24h", { defaultValue: "24u levering" })}</div>
                    <div className="flex items-center gap-2"><Shield size={14} /> {t("product.certified", { defaultValue: "Gecertificeerd" })}</div>
                    <div className="flex items-center gap-2"><Factory size={14} /> {t("product.made_in_nl", { defaultValue: "Made in NL" })}</div>
                  </div>

                  {/* Enriched variant specs strip (V2 pricelist data) */}
                  {(variant.bolts_nuts || variant.stud_size || variant.thread_rod_size || variant.hole_d2_mm || variant.strip_size) && (
                    <div
                      className="mt-4 grid grid-cols-2 gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs sm:grid-cols-3"
                      data-testid="variant-specs-grid"
                    >
                      {variant.strip_size && (
                        <SpecCell label="Stripmaat" value={variant.strip_size} testid="spec-strip-size" />
                      )}
                      {variant.bolts_nuts && (
                        <SpecCell label="Bouten + moeren" value={variant.bolts_nuts} testid="spec-bolts-nuts" />
                      )}
                      {variant.stud_size && (
                        <SpecCell label="Stift" value={variant.stud_size} testid="spec-stud-size" />
                      )}
                      {variant.thread_rod_size && (
                        <SpecCell label="Draadstang" value={variant.thread_rod_size} testid="spec-thread-rod" />
                      )}
                      {variant.hole_d2_mm && (
                        <SpecCell label="Boorgat Ø d₂" value={typeof variant.hole_d2_mm === "number" ? `${variant.hole_d2_mm}mm` : variant.hole_d2_mm} testid="spec-hole-d2" />
                      )}
                      {variant.weight_g > 0 && (
                        <SpecCell label="Gewicht / stuk" value={`${variant.weight_g} g`} testid="spec-weight" />
                      )}
                    </div>
                  )}

                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/datasheet.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 rounded-md border border-nibro-sage bg-white px-4 py-2 text-xs font-bold text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
                    data-testid="datasheet-pdf-link"
                  >
                    📄 Download technische datasheet (PDF)
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* H vs P (single bracket vs full set) comparator — DIN 3567 only */}
        <HpComparator product={product} dnContext={dnContext} />

        {/* Compare materials widget */}
        <MaterialComparison productId={product.id} currentMaterial={product.material} dnContext={dnContext} />
      </div>

      {/* Mobile sticky add-to-cart bar (only ≤sm) */}
      {variant && (
        <div
          className={`fixed inset-x-0 bottom-0 z-50 border-t-2 border-nibro-sage bg-white/95 px-3 py-2 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)] backdrop-blur-md transition-transform duration-300 ease-out sm:hidden ${
            stickyVisible ? "translate-y-0" : "translate-y-full"
          }`}
          data-testid="mobile-sticky-cart-bar"
          data-visible={stickyVisible ? "true" : "false"}
        >
          <div className="flex items-stretch gap-2 pr-16">
            {/* Price + qty stack */}
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <div className="flex items-baseline gap-1.5">
                <span className="font-display text-xl font-bold text-zinc-900 leading-none" data-testid="mobile-sticky-price">
                  €{effectiveUnitPrice.toFixed(2)}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">/ stuk</span>
                {hasBoxDeal && fullBoxAligned && (
                  <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 font-mono text-[9px] font-bold text-emerald-700">
                    −{boxDiscountPct.toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="mt-0.5 font-mono text-[10px] text-zinc-500 truncate">
                {variant.sku} · Ø{variant.diameter_mm}mm{variant.dn ? ` · DN${variant.dn}` : ""}
              </div>
            </div>
            {/* Qty stepper */}
            <div className="flex items-center self-center rounded-md border border-zinc-200 bg-white">
              <button
                type="button"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-2.5 py-2 text-zinc-500 active:bg-zinc-100"
                data-testid="mobile-sticky-qty-minus"
                aria-label="Minder"
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-10 bg-transparent text-center font-mono text-sm font-bold text-zinc-900 focus:outline-none"
                data-testid="mobile-sticky-qty-input"
                aria-label="Aantal"
              />
              <button
                type="button"
                onClick={() => setQty(qty + 1)}
                className="px-2.5 py-2 text-zinc-500 active:bg-zinc-100"
                data-testid="mobile-sticky-qty-plus"
                aria-label="Meer"
              >
                <Plus size={14} />
              </button>
            </div>
            {/* CTA */}
            <button
              type="button"
              onClick={handleAdd}
              className={`flex items-center gap-1.5 rounded-md px-3 text-xs font-bold transition ${
                added ? "bg-emerald-500 text-white" : "bg-nibro-sage text-white active:bg-nibro-sage-hover"
              }`}
              data-testid="mobile-sticky-add-button"
            >
              {added ? (
                <><Check size={14} /></>
              ) : (
                <><ShoppingCart size={14} /> Voeg toe</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== Product structured data for AI bots & SEO ============== */
function ProductSeo({ product, variant, effectiveUnitPrice }) {
  const base = (typeof window !== "undefined" && window.location.origin) || "";
  const url = product ? `${base}/products/${product.id}` : "";
  const imageAbs = product?.image
    ? (product.image.startsWith("http") ? product.image : `${base}${product.image}`)
    : "";
  const matMap = { 304: "1.4301 (V2A-304)", 316: "1.4404 (V4A-316L)", duplex: "1.4462 (Duplex)", galvanized: "Galvanized steel" };
  const matLabel = product ? (matMap[String(product.material || "").toLowerCase()] || product.material) : "";

  const offers = (product?.variants || []).slice(0, 8).map((v) => ({
    "@type": "Offer",
    "sku": v.sku,
    "name": `${product.name_nl || product.name} Ø${v.diameter_mm}mm${v.dn ? ` DN${v.dn}` : ""}`,
    "priceCurrency": "EUR",
    "price": v.price?.toFixed(2),
    "url": `${url}${v.dn ? `?dn=${v.dn}` : ""}`,
    "availability": "https://schema.org/InStock",
    "itemCondition": "https://schema.org/NewCondition",
    "priceValidUntil": new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
  }));

  const productLd = product ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name_nl || product.name,
    "description": product.description || `${matLabel} stainless steel pipe clamp · ${product.kit_label_nl || product.kit_type || ""}`,
    "sku": product.id,
    "category": product.category,
    "brand": { "@type": "Brand", "name": "NIBRO" },
    "manufacturer": { "@type": "Organization", "name": "NIBRO", "url": "https://stainlesspipeclamps.store/" },
    "image": imageAbs ? [imageAbs] : undefined,
    "url": url,
    "material": matLabel,
    "additionalProperty": [
      product.standard && { "@type": "PropertyValue", "name": "Standard", "value": product.standard },
      product.strip_size && { "@type": "PropertyValue", "name": "Strip size", "value": product.strip_size },
      { "@type": "PropertyValue", "name": "Variant count", "value": product.variant_count },
    ].filter(Boolean),
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": product.price_from?.toFixed(2),
      "highPrice": product.price_to?.toFixed(2),
      "offerCount": product.variant_count || (product.variants || []).length,
      "offers": offers,
    },
  } : null;

  const breadcrumbLd = product ? {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base + "/" },
      { "@type": "ListItem", "position": 2, "name": product.category || "Producten", "item": `${base}/products` },
      { "@type": "ListItem", "position": 3, "name": product.name_nl || product.name, "item": url },
    ],
  } : null;

  const title = product ? `${product.name_nl || product.name} · ${matLabel} · NIBRO` : "";
  const desc = product
    ? (product.description
        ? product.description.slice(0, 155)
        : `${product.name_nl || product.name} in ${matLabel}. €${product.price_from?.toFixed(2)} – €${product.price_to?.toFixed(2)} · ${product.variant_count} maten · 24u levering NL/EU.`)
    : "";

  useSeoHead({
    title,
    description: desc,
    canonical: url,
    meta: product ? [
      { property: "og:type", content: "product" },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:url", content: url },
      ...(imageAbs ? [{ property: "og:image", content: imageAbs }] : []),
      { property: "product:price:amount", content: (effectiveUnitPrice || product.price_from || 0).toFixed(2) },
      { property: "product:price:currency", content: "EUR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
      ...(imageAbs ? [{ name: "twitter:image", content: imageAbs }] : []),
    ] : [],
    jsonLd: productLd ? [productLd, breadcrumbLd] : [],
  });

  return null;
}
